/**
 * @author jakubbujakowski
 * @since 2020-11-30
 */

import React, { ReactElement } from "react";
import styled, { css } from "styled-components";
import { NavLink as Link } from "react-router-dom";

import theme from "@ui/themes/default";
import { Caret } from "@ui/Assets/Symbolicons/Caret";
import { OptionPanel, Heading, TestWrapper, DividerLine } from "@ui/Atoms";
import { rem } from "@ui/helpers";

interface Props {
  children?: React.ReactNode;
  url: string;
  headingText: string;
  testId?: string;
  icon?: React.ReactElement;
  withNativeLink?: boolean;
  onButtonClick?: () => void;
  className?: string;
  withoutDivideLine?: boolean;
}

const OptionPanelLink = (props: Props): ReactElement => {
  if (props.withNativeLink) {
    return (
      <React.Fragment>
        <StyledPanel
          className={props.className}
          withoutDivideLine={props.withoutDivideLine}
        >
          <TestWrapper testId={props.testId}>
            <StyledNativeLink onClick={props.onButtonClick}>
              <TitleWithIcon>
                {props.icon && <IconWrapper>{props.icon}</IconWrapper>}
                <Title type="h6">{props.headingText}</Title>
              </TitleWithIcon>
              <CaretStyled>
                <Caret size={24} color={theme.palette.grey800} variant="line" />
              </CaretStyled>
            </StyledNativeLink>
          </TestWrapper>
        </StyledPanel>

        {!props.withoutDivideLine && <DividerLine withFullWidth />}
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <StyledPanel className={props.className}>
        <TestWrapper testId={props.testId}>
          <StyledLink to={props.url}>
            <TitleWithIcon>
              {props.icon && <IconWrapper>{props.icon}</IconWrapper>}
              <Title type="h6">{props.headingText}</Title>
            </TitleWithIcon>
            <CaretStyled>
              <Caret size={24} color={theme.palette.grey800} variant="line" />
            </CaretStyled>
          </StyledLink>
        </TestWrapper>
      </StyledPanel>
      <DividerLine withFullWidth />
    </React.Fragment>
  );
};

export { OptionPanelLink };

const TitleWithIcon = styled.div`
  display: flex;
  align-items: center;
`;

const Title = styled(Heading)`
  margin: 0;
  font-weight: ${props => props.theme.fonts.weights.normal};
  font-size: ${props => props.theme.fonts.sizes.body};
`;

const IconWrapper = styled.div`
  margin-right: ${props => props.theme.margins.base_x2};
`;

const CaretStyled = styled.div`
  svg {
    transform: rotate(-90deg);
  }
`;

const linkStyles = css`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
`;

const StyledLink = styled(Link)`
  ${linkStyles}
`;

const StyledNativeLink = styled.button`
  ${linkStyles}
  padding: 0;
`;

const StyledPanel = styled(OptionPanel)<{ withoutDivideLine?: boolean }>`
  display: flex;
  margin: 0;
  padding: ${props => props.theme.margins.base_x2} 0;
  height: ${rem(72)};

  ${props =>
    props.withoutDivideLine &&
    css`
      padding-bottom: none;
    `}
`;
